import { useMemo, useState } from 'react'
import {Copy, Download, Printer, RotateCcw, Share2} from 'lucide-react'
import toast from 'react-hot-toast'
import Breadcrumbs from '@/components/Breadcrumbs'
import DisclaimerNote from '@/components/DisclaimerNote'
import FaqAccordion from '@/components/FaqAccordion'
import NumberInput from '@/components/ui/NumberInput'
import ProgressBar from '@/components/ui/ProgressBar'
import SectionHeading from '@/components/ui/SectionHeading'
import StatCard from '@/components/ui/StatCard'
import Seo from '@/components/Seo'
import { formatCurrency, formatPercent } from '@/lib/format'
import { breadcrumbSchema, faqPageSchema, softwareApplicationSchema } from '@/lib/schema'
import { exportResultsToPdf } from '@/lib/pdfExport'

interface BudgetState {
  primaryIncome: number
  sideIncome: number
  housing: number
  utilities: number
  groceries: number
  transport: number
  insurance: number
  dining: number
  entertainment: number
  shopping: number
  subscriptions: number
  retirement401k: number
  emergencyFund: number
  extraDebtPayoff: number
}

const defaults: BudgetState = {
  primaryIncome: 5200,
  sideIncome: 400,
  housing: 1500,
  utilities: 220,
  groceries: 500,
  transport: 300,
  insurance: 250,
  dining: 300,
  entertainment: 150,
  shopping: 250,
  subscriptions: 80,
  retirement401k: 400,
  emergencyFund: 300,
  extraDebtPayoff: 200,
}

const faqItems = [
  { question: 'What is the 50/30/20 budget rule?', answer: 'The 50/30/20 rule allocates 50% of take-home pay to Needs (housing, groceries, utilities), 30% to Wants (dining, entertainment, shopping), and 20% to Savings & Debt Payoff (retirement, emergency fund, extra debt principal).' },
  { question: 'What counts as a "Need" vs a "Want"?', answer: 'Needs are non-negotiable essentials like rent, utilities, groceries, transport, and insurance. Wants are discretionary spending like dining out, streaming subscriptions, entertainment, and shopping beyond necessities.' },
  { question: 'How is zero-based budgeting different?', answer: 'Zero-based budgeting assigns every dollar of income a specific job — Needs, Wants, or Savings — until the unallocated balance reaches zero, rather than following fixed percentage targets.' },
  { question: 'Is Mint Calculator a replacement for the Intuit Mint app?', answer: 'Yes. Mint Calculator was built for users displaced by the shutdown of Intuit Mint, offering free budgeting, loan, and net worth calculators with no account required.' },
]

function Budget503020() {
  const [state, setState] = useState<BudgetState>(defaults)

  const update = (key: keyof BudgetState) => (value: number) => setState(prev => ({ ...prev, [key]: value }))

  const results = useMemo(() => {
    const totalIncome = state.primaryIncome + state.sideIncome
    const needs = state.housing + state.utilities + state.groceries + state.transport + state.insurance
    const wants = state.dining + state.entertainment + state.shopping + state.subscriptions
    const savings = state.retirement401k + state.emergencyFund + state.extraDebtPayoff
    const allocated = needs + wants + savings
    const unallocated = totalIncome - allocated

    const pct = (value: number) => totalIncome > 0 ? (value / totalIncome) * 100 : 0

    const targets = { needs: 50, wants: 30, savings: 20 }
    const actual = { needs: pct(needs), wants: pct(wants), savings: pct(savings) }

    const emergencyMonthlyEssential = needs
    const emergencyBufferLow = emergencyMonthlyEssential * 3
    const emergencyBufferHigh = emergencyMonthlyEssential * 6

    const monthlyRate = 0.07 / 12
    const months = 120
    let futureValue = 0
    for (let i = 0; i < months; i++) {
      futureValue = (futureValue + savings) * (1 + monthlyRate)
    }

    return {
      totalIncome,
      needs,
      wants,
      savings,
      allocated,
      unallocated,
      actual,
      targets,
      emergencyBufferLow,
      emergencyBufferHigh,
      futureValue,
    }
  }, [state])

  const handleReset = () => {
    setState(defaults)
    toast.success('Reset to default values')
  }

  const handleCopy = () => {
    const summary = `Mint Calculator - Budget Summary
Total Income: ${formatCurrency(results.totalIncome)}
Needs: ${formatCurrency(results.needs)} (${formatPercent(results.actual.needs)})
Wants: ${formatCurrency(results.wants)} (${formatPercent(results.actual.wants)})
Savings/Debt: ${formatCurrency(results.savings)} (${formatPercent(results.actual.savings)})
Unallocated: ${formatCurrency(results.unallocated)}`
    navigator.clipboard.writeText(summary)
    toast.success('Summary copied to clipboard')
  }

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href)
    toast.success('Link copied to clipboard')
  }

  const handleDownloadPdf = () => {
    exportResultsToPdf({
      title: '50/30/20 Budget & Zero-Based Cash Flow Report',
      subtitle: 'Personal budgeting summary generated by Mint Calculator',
      fileName: 'mint-calculator-budget-summary.pdf',
      sections: [
        {
          heading: 'Income & Allocation Summary',
          rows: [
            ['Total Income', formatCurrency(results.totalIncome)],
            ['Needs', `${formatCurrency(results.needs)} (${formatPercent(results.actual.needs)})`],
            ['Wants', `${formatCurrency(results.wants)} (${formatPercent(results.actual.wants)})`],
            ['Savings / Debt Payoff', `${formatCurrency(results.savings)} (${formatPercent(results.actual.savings)})`],
            ['Unallocated', formatCurrency(results.unallocated)],
          ],
        },
        {
          heading: 'Emergency Fund & Growth Projection',
          rows: [
            ['Emergency Fund Buffer (3-6 months)', `${formatCurrency(results.emergencyBufferLow)} - ${formatCurrency(results.emergencyBufferHigh)}`],
            ['10-Year Growth Projection (7% avg return)', formatCurrency(results.futureValue)],
          ],
        },
      ],
    })
    toast.success('PDF downloaded')
  }

  const variance = (actual: number, target: number) => actual - target

  return (
    <>
      <Seo
        title="50/30/20 Budget Calculator - Free Zero-Based Cash Flow Planner"
        description="Free 50/30/20 budget calculator with zero-based cash flow tracking, real-time surplus/deficit, emergency fund targets, and 10-year compound growth projections."
        path="/calculators/budget-50-30-20"
        schema={[
          softwareApplicationSchema('50/30/20 Budget Calculator', 'Real-time budgeting calculator using the 50/30/20 rule with zero-based cash flow analytics.', 'https://www.mintcalculator.com/calculators/budget-50-30-20'),
          faqPageSchema(faqItems),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Budget 50/30/20', path: '/calculators/budget-50-30-20' }]),
        ]}
      />

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <Breadcrumbs items={[{ name: 'Home', path: '/' }, { name: 'Budget 50/30/20 Calculator', path: '/calculators/budget-50-30-20' }]} />

        <SectionHeading
          eyebrow="Personal Budgeting & Net Worth"
          title="50/30/20 Budget & Zero-Based Cash Flow Calculator"
          description="Enter your income and categorized expenses to see real-time surplus tracking, 50/30/20 variance analysis, and long-term growth projections."
        />

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-5">
          <div className="space-y-6 lg:col-span-3">
            <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">Income</h3>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <NumberInput label="Primary net take-home pay" value={state.primaryIncome} onChange={update('primaryIncome')} prefix="$" step={50} accent="emerald" />
                <NumberInput label="Side hustle / investment income" value={state.sideIncome} onChange={update('sideIncome')} prefix="$" step={25} accent="emerald" />
              </div>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">Needs (Target 50%)</h3>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <NumberInput label="Housing" value={state.housing} onChange={update('housing')} prefix="$" step={25} accent="cyan" />
                <NumberInput label="Utilities" value={state.utilities} onChange={update('utilities')} prefix="$" step={10} accent="cyan" />
                <NumberInput label="Groceries" value={state.groceries} onChange={update('groceries')} prefix="$" step={25} accent="cyan" />
                <NumberInput label="Transport" value={state.transport} onChange={update('transport')} prefix="$" step={25} accent="cyan" />
                <NumberInput label="Insurance" value={state.insurance} onChange={update('insurance')} prefix="$" step={25} accent="cyan" />
              </div>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">Wants (Target 30%)</h3>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <NumberInput label="Dining" value={state.dining} onChange={update('dining')} prefix="$" step={25} accent="amber" />
                <NumberInput label="Entertainment" value={state.entertainment} onChange={update('entertainment')} prefix="$" step={10} accent="amber" />
                <NumberInput label="Shopping" value={state.shopping} onChange={update('shopping')} prefix="$" step={25} accent="amber" />
                <NumberInput label="Subscriptions" value={state.subscriptions} onChange={update('subscriptions')} prefix="$" step={5} accent="amber" />
              </div>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">Savings & Debt (Target 20%)</h3>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <NumberInput label="401k Contribution" value={state.retirement401k} onChange={update('retirement401k')} prefix="$" step={25} accent="teal" />
                <NumberInput label="Emergency Fund" value={state.emergencyFund} onChange={update('emergencyFund')} prefix="$" step={25} accent="teal" />
                <NumberInput label="Extra Debt Payoff" value={state.extraDebtPayoff} onChange={update('extraDebtPayoff')} prefix="$" step={25} accent="teal" />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button onClick={handleCopy} className="flex items-center gap-2 rounded-lg border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:border-emerald-500/50 hover:text-emerald-400">
                <Copy className="h-4 w-4" />
                Copy Summary
              </button>
              <button onClick={handleShare} className="flex items-center gap-2 rounded-lg border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:border-emerald-500/50 hover:text-emerald-400">
                <Share2 className="h-4 w-4" />
                Share URL
              </button>
              <button onClick={handleReset} className="flex items-center gap-2 rounded-lg border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:border-emerald-500/50 hover:text-emerald-400">
                <RotateCcw className="h-4 w-4" />
                Reset Defaults
              </button>
              <button onClick={() => window.print()} className="flex items-center gap-2 rounded-lg border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:border-emerald-500/50 hover:text-emerald-400">
                <Printer className="h-4 w-4" />
                Print / Export
              </button>
              <button onClick={handleDownloadPdf} className="flex items-center gap-2 rounded-lg border border-emerald-600/60 bg-emerald-500/10 px-4 py-2 text-sm font-medium text-emerald-400 transition-colors hover:border-emerald-500 hover:bg-emerald-500/20">
                <Download className="h-4 w-4" />
                Download PDF
              </button>
            </div>
          </div>

          <div className="space-y-6 lg:col-span-2">
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Total Income" value={formatCurrency(results.totalIncome)} accent="slate" />
              <StatCard label="Unallocated" value={formatCurrency(results.unallocated)} accent={results.unallocated >= 0 ? 'emerald' : 'rose'} sublabel={results.unallocated >= 0 ? 'Surplus' : 'Deficit'} />
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
              <h3 className="text-sm font-semibold text-slate-300">Live Distribution</h3>
              <div className="mt-4">
                <ProgressBar
                  total={results.allocated || 1}
                  segments={[
                    { label: 'Needs', value: results.needs, color: 'bg-cyan-500' },
                    { label: 'Wants', value: results.wants, color: 'bg-amber-500' },
                    { label: 'Savings/Debt', value: results.savings, color: 'bg-teal-500' },
                  ]}
                />
              </div>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
              <h3 className="text-sm font-semibold text-slate-300">50/30/20 Target vs Actual</h3>
              <div className="mt-4 space-y-3">
                {[
                  { label: 'Needs', actual: results.actual.needs, target: results.targets.needs, color: 'text-cyan-400' },
                  { label: 'Wants', actual: results.actual.wants, target: results.targets.wants, color: 'text-amber-400' },
                  { label: 'Savings/Debt', actual: results.actual.savings, target: results.targets.savings, color: 'text-teal-400' },
                ].map((row) => {
                  const diff = variance(row.actual, row.target)
                  return (
                    <div key={row.label} className="flex items-center justify-between rounded-lg bg-slate-800/50 px-3 py-2.5">
                      <span className="text-sm text-slate-300">{row.label}</span>
                      <div className="flex items-center gap-3">
                        <span className={`font-mono text-sm ${row.color}`}>{formatPercent(row.actual)}</span>
                        <span className="font-mono text-xs text-slate-500">vs {row.target}%</span>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-mono ${diff > 0 ? 'bg-rose-500/10 text-rose-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
                          {diff > 0 ? '+' : ''}
                          {diff.toFixed(1)}
                          %
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
              <h3 className="text-sm font-semibold text-slate-300">Emergency Fund Buffer Recommendation</h3>
              <p className="mt-2 text-xs text-slate-500">Based on your monthly Needs spending (3-6 months)</p>
              <div className="mt-3 flex items-center justify-between font-mono text-sm">
                <span className="text-slate-400">{formatCurrency(results.emergencyBufferLow)}</span>
                <span className="text-slate-600">to</span>
                <span className="text-emerald-400">{formatCurrency(results.emergencyBufferHigh)}</span>
              </div>
            </div>

            <StatCard label="10-Year Growth Projection (7% avg return)" value={formatCurrency(results.futureValue)} sublabel="If current savings/debt allocation is invested monthly" accent="emerald" size="lg" />

            <DisclaimerNote />
          </div>
        </div>

        <div className="mt-16 max-w-3xl">
          <SectionHeading title="Frequently Asked Questions" />
          <div className="mt-6">
            <FaqAccordion items={faqItems} />
          </div>
        </div>
      </div>
    </>
  )
}

export default Budget503020
